import Excel from 'exceljs';
import dayjs from 'dayjs';
import { BUSINESS_TZ } from './formatting/format-date';

// Excel number/date display formats. Cells carry RAW values (number / Date) and
// get their look from `numFmt`, so the recipient can SUM(), sort and filter them.
// Pre-formatting with `formatFloat` produced text cells that Excel refuses to add up.
export const EXCEL_NUMBER_FORMAT = '#,##0.00';
export const EXCEL_DATE_FORMAT = 'yyyy-mm-dd';
export const EXCEL_DATE_TIME_FORMAT = 'yyyy-mm-dd hh:mm:ss';

// exceljs converts a Date to an Excel serial straight from `getTime()` (UTC based,
// see exceljs/lib/utils/utils.js `dateToExcel`). So the Date we hand it must carry
// the value we want DISPLAYED in its UTC fields, not in local ones.

// CALENDAR DATE field (sale/expense `date`): a bare day stamped at midnight UTC.
// Read it back as UTC so the day never shifts for the viewer, then rebuild it as
// UTC midnight -> exact integer serial in Excel.
export function toExcelCalendarDate(date?: string | null): Date | null {
    if (!date) return null;
    const day = dayjs.utc(date);
    if (!day.isValid()) return null;
    return new Date(
        Date.UTC(day.year(), day.month(), day.date(), 0, 0, 0),
    );
}

// INSTANT field (`transferred_date`): a true UTC instant shown in Mexico
// business time on screen. Convert to the fixed application timezone, then
// re-stamp those wall-clock parts as UTC so Excel shows the same hour the app
// does even when the user is travelling.
export function toExcelInstant(date?: string | null): Date | null {
    if (!date) return null;
    const businessTime = dayjs.utc(date).tz(BUSINESS_TZ);
    if (!businessTime.isValid()) return null;
    return new Date(
        Date.UTC(
            businessTime.year(),
            businessTime.month(),
            businessTime.date(),
            businessTime.hour(),
            businessTime.minute(),
            businessTime.second(),
        ),
    );
}

/** Right-aligns the given 1-based columns and gives them a numeric display format. */
export function styleNumberColumns(
    ws: Excel.Worksheet,
    columns: number[],
    numFmt: string = EXCEL_NUMBER_FORMAT,
): void {
    columns.forEach((index) => {
        const column = ws.getColumn(index);
        column.numFmt = numFmt;
        column.alignment = { horizontal: 'right' };
    });
}

/** Centers the given 1-based columns and gives them a date display format. */
export function styleDateColumns(
    ws: Excel.Worksheet,
    columns: number[],
    numFmt: string = EXCEL_DATE_FORMAT,
): void {
    columns.forEach((index) => {
        const column = ws.getColumn(index);
        column.numFmt = numFmt;
        column.alignment = { horizontal: 'center' };
    });
}

/** Bolds a row (the header row, or a totals row) in place. */
export function boldRow(row: Excel.Row): Excel.Row {
    row.font = { bold: true };
    return row;
}

/**
 * Appends a bold summary row. `values` follows the sheet's column order; pass
 * null for columns that have no total.
 */
export function addTotalsRow(
    ws: Excel.Worksheet,
    values: (string | number | null)[],
): Excel.Row {
    const row = ws.addRow(values.map((value) => (value === null ? '' : value)));
    row.border = { top: { style: 'thin' } };
    return boldRow(row);
}

// How wide a cell READS once Excel applies its format. A Date's `toString()` is
// ~55 chars ("Wed Jul 15 2026 00:00:00 GMT-0600 ..."), which would blow the
// column out to the width of a string nobody sees.
function displayLength(cell: Excel.Cell): number {
    const value = cell.value;
    if (value === null || value === undefined) return 0;
    if (value instanceof Date) {
        return (cell.numFmt || EXCEL_DATE_TIME_FORMAT).length;
    }
    if (typeof value === 'number') {
        return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',').length;
    }
    return value.toString().length;
}

/**
 * Widens every column in `ws` to fit its longest cell value (plus 2 chars of
 * breathing room). Call this after all rows have been added.
 */
export function autoSizeColumns(ws: Excel.Worksheet): void {
    ws.columns.forEach((c, i) => {
        let maxColumnLength = 6;
        const column = ws.getColumn(i + 1);
        if (column) {
            column.eachCell({ includeEmpty: true }, (cell) => {
                maxColumnLength = Math.max(maxColumnLength, displayLength(cell));
            });
            column.width = maxColumnLength + 2;
        }
    });
}
